import {useEffect, useRef} from 'react'
import {Linking, View} from 'react-native'
import {Image} from 'expo-image'

import {atoms as a, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'
import {useAnnouncementsContext} from '#/features/announcements/context'
import {type Announcement} from '#/features/announcements/types'
import {useSemanticTokens} from '#/wsocial/service/design-system'

// On web, React Navigation fires an init 'state' event that triggers
// closeAllActiveElements(), instantly closing any newly-opened dialog.
// Delay past that event so the announcement actually appears.
const OPEN_DELAY_MS = IS_WEB ? 500 : 0

function openUrl(url: string, openInNewTab?: boolean) {
  if (IS_WEB) {
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = url
    }
  } else {
    void Linking.openURL(url)
  }
}

function AnnouncementDialog({announcement}: {announcement: Announcement}) {
  const s = useSemanticTokens()
  const control = Dialog.useDialogControl()
  const {markShown} = useAnnouncementsContext()

  Dialog.useAutoOpen(control, OPEN_DELAY_MS)

  const primaryButtonRef = useRef<View>(null)
  useEffect(() => {
    if (!IS_WEB) return
    const t = setTimeout(() => {
      ;(primaryButtonRef.current as unknown as HTMLElement)?.focus?.()
    }, OPEN_DELAY_MS + 50)
    return () => clearTimeout(t)
  }, [])

  const onClose = () => {
    markShown(announcement.id)
  }

  const onPressPrimary = () => {
    control.close(() => {
      openUrl(
        announcement.primaryButton.url!,
        announcement.primaryButton.openInNewTab,
      )
    })
  }

  const onPressSecondary = () => {
    if (announcement.secondaryButton.url) {
      control.close(() => {
        openUrl(
          announcement.secondaryButton.url!,
          announcement.secondaryButton.openInNewTab,
        )
      })
    } else {
      control.close()
    }
  }

  return (
    <Dialog.Outer control={control} onClose={onClose}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        label={announcement.header}
        style={[
          announcement.width ? web({maxWidth: announcement.width}) : undefined,
          announcement.height ? web({height: announcement.height}) : undefined,
        ]}
        contentContainerStyle={web({flex: 1, flexDirection: 'column'})}>
        <Dialog.Close />
        <Image
          source={announcement.image}
          style={[
            a.w_full,
            a.rounded_md,
            a.mt_2xl,
            {aspectRatio: announcement.imageAspectRatio ?? 1.7},
          ]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
        />
        <View style={[a.flex_1, a.py_2xl, a.gap_sm, a.align_center]}>
          <Text style={[a.text_sm, a.text_center, {color: s.link.text}]}>
            {announcement.preHeader}
          </Text>
          <Text
            style={[
              a.text_4xl,
              a.font_semi_bold,
              a.text_center,
              {color: s.text.default},
            ]}>
            {announcement.header}
          </Text>
          <Text style={[a.text_md, a.text_center, {color: s.text.default}]}>
            {announcement.text}
          </Text>
        </View>
        <View style={[a.gap_md]}>
          <Button
            ref={primaryButtonRef}
            label={announcement.primaryButton.label}
            onPress={onPressPrimary}
            color="primary"
            size="large"
            shape="default">
            <ButtonText>{announcement.primaryButton.label}</ButtonText>
          </Button>
          <Button
            label={announcement.secondaryButton.label}
            onPress={onPressSecondary}
            color="secondary"
            size="large"
            shape="default">
            <ButtonText>{announcement.secondaryButton.label}</ButtonText>
          </Button>
        </View>
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

export function AnnouncementDialogs() {
  const {queue} = useAnnouncementsContext()
  const current = queue[0]
  if (!current) return null
  // key resets the component (and its useDialogControl) when announcement changes
  return <AnnouncementDialog key={current.id} announcement={current} />
}
